/**
 * Syncs the native product price with the selected Kaching Bundles deal (incl. discount).
 */
(function () {
  const POLL_MS = 200;
  const POLL_DURATION_MS = 12000;
  const setupPages = new WeakSet();
  const lastSyncedByPage = new WeakMap();

  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '€{{amount_with_comma_separator}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return '0';
      }

      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    let value = '';
    const formatKey = formatString.match(placeholderRegex)?.[1] || 'amount_with_comma_separator';

    switch (formatKey) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2, '.', ',');
    }

    return formatString.replace(placeholderRegex, value);
  }

  function parseDiscountPercent(label) {
    if (!label) return 0;
    const match = String(label).match(/(\d+(?:[.,]\d+)?)\s*%/);
    return match ? parseFloat(match[1].replace(',', '.')) : 0;
  }

  /* 5 Oct 2026: the current Kaching embed puts the deal config on the
     <kaching-bundles-block deal-block="..."> attribute instead of a script tag, so read both. */
  function getDealConfig(productPage) {
    const script = productPage.querySelector('script.kaching-bundles-deal-block-settings');
    const block = productPage.querySelector('kaching-bundles-block[deal-block]');
    const raw = script ? script.textContent : block ? block.getAttribute('deal-block') : '';
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function hasKaching(productPage) {
    return !!productPage.querySelector(
      'script.kaching-bundles-deal-block-settings, kaching-bundles-block[deal-block]'
    );
  }

  function getUnitPriceCents(productPage) {
    const variantsScript = productPage.querySelector('product-variants script[type="application/json"]');
    if (variantsScript) {
      try {
        const variants = JSON.parse(variantsScript.textContent);
        const formVariantInput = productPage.querySelector(
          'form[data-type="add-to-cart-form"] input[name="id"]'
        );
        const selectedId = formVariantInput ? Number(formVariantInput.value) : null;
        const productVariants = productPage.querySelector('product-variants');
        const variantId = selectedId || productVariants?.currentVariant?.id;
        const variant =
          variants.find((item) => item.id === variantId) ||
          variants.find((item) => item.available) ||
          variants[0];
        if (variant?.price) return variant.price;
      } catch (error) {
        /* fall through */
      }
    }

    const kachingProduct = productPage.querySelector('script.kaching-bundles-product');
    if (kachingProduct) {
      try {
        const product = JSON.parse(kachingProduct.textContent);
        const variant =
          product.variants?.find((item) => item.id === product.selectedVariantId) ||
          product.variants?.[0];
        if (variant?.price) return variant.price;
      } catch (error) {
        /* fall through */
      }
    }

    const kachingBlock = productPage.querySelector('kaching-bundles-block[product]');
    if (kachingBlock) {
      try {
        const product = JSON.parse(kachingBlock.getAttribute('product'));
        const currentId = Number(kachingBlock.getAttribute('current-variant-id')) || product.selectedVariantId;
        const variant =
          product.variants?.find((item) => item.id === currentId) || product.variants?.[0];
        if (variant?.price) return variant.price;
      } catch (error) {
        /* fall through */
      }
    }

    return productPage.querySelector('product-variants')?.currentVariant?.price || 0;
  }

  function getDealId(productPage, config) {
    const selectedBar = productPage.querySelector('.kaching-bundles__bar--selected');
    if (selectedBar?.dataset.dealBarId) {
      return selectedBar.dataset.dealBarId;
    }

    const checkedRadio = productPage.querySelector('.kaching-bundles input[type="radio"]:checked');
    if (checkedRadio?.value) {
      return checkedRadio.value;
    }

    const bundleInput = productPage.querySelector('input[name="properties[__kaching_bundles]"]');
    if (bundleInput?.value) {
      try {
        const parsed = JSON.parse(bundleInput.value);
        if (parsed.bid) return parsed.bid;
      } catch (error) {
        /* fall through */
      }
    }

    return config?.preselectedDealBarId || config?.dealBars?.[0]?.id || null;
  }

  /* 5 Oct 2026: price mode comes from the theme setting "Top price shows" (item or total). */
  const PRICE_MODE =
    (document.currentScript && document.currentScript.dataset.priceMode) || 'total';

  function parseMoneyCents(text) {
    if (!text) return 0;
    const match = String(text).match(/\d[\d.,\s'\u00a0]*/);
    if (!match) return 0;
    const raw = match[0].trim();
    const digits = raw.replace(/\D/g, '');
    if (!digits) return 0;
    return /[.,]\d{2}$/.test(raw) ? parseInt(digits, 10) : parseInt(digits, 10) * 100;
  }

  function getSelectedBar(productPage, dealId) {
    if (dealId) {
      const byId = productPage.querySelector(`.kaching-bundles__bar[data-deal-bar-id="${dealId}"]`);
      if (byId) return byId;
    }
    return productPage.querySelector('.kaching-bundles__bar--selected');
  }

  /* Per piece price from the Kaching config. Kaching stores fixed amounts in currency units,
     the old script read every discountValue as a percentage (1 euro off became 1 percent off). */
  function getConfigItemPrice(deal, unitPrice) {
    let type = String(deal.discountType || deal.discount_type || '').toLowerCase();
    let value = Number(deal.discountValue) || 0;
    if (value <= 0) {
      value = parseDiscountPercent(deal.label);
      type = 'percentage';
    }
    if (value <= 0) return unitPrice;
    if (type.indexOf('fixed') > -1 || type.indexOf('amount') > -1) {
      return Math.max(0, unitPrice - Math.round(value * 100));
    }
    if (type.indexOf('specific') > -1 || type === 'price') {
      return Math.round(value * 100);
    }
    return Math.round(unitPrice * (1 - value / 100));
  }

  /* Per piece price exactly as the selected Kaching bar shows it. */
  function getBarItemPrice(bar, unitPrice, quantity) {
    if (!bar) return 0;
    const price = parseMoneyCents(bar.querySelector('.kaching-bundles__bar-price')?.textContent);
    if (!price) return 0;
    if (quantity <= 1) return price;
    const full = parseMoneyCents(bar.querySelector('.kaching-bundles__bar-full-price')?.textContent);
    const reference = full || price;
    const showsTotal =
      Math.abs(reference - unitPrice * quantity) < Math.abs(reference - unitPrice);
    return showsTotal ? Math.round(price / quantity) : price;
  }

  /* 5 Oct 2026 (Derk, Zelf kiezen): a bar with "Hoeveelheidskeuze tonen" lets the customer
     pick the amount inside the bar, so the quantity is read live from that bar and only falls
     back to the fixed quantity from the Kaching config. */
  function getLiveQuantity(bar, fallback) {
    if (!bar) return fallback;
    const field = bar.querySelector(
      'input[type="number"], input[name*="quantity" i], input[class*="quantity" i], select[class*="quantity" i]'
    );
    const fromField = field ? parseInt(field.value, 10) : NaN;
    if (fromField > 0) return fromField;
    const nodes = bar.querySelectorAll('[class*="quantity" i], [class*="qty" i]');
    for (const node of nodes) {
      if (node.children.length) continue;
      const text = (node.textContent || '').trim();
      if (/^\d{1,3}$/.test(text) && parseInt(text, 10) > 0) return parseInt(text, 10);
    }
    return fallback;
  }

  function getDealPrices(productPage, config) {
    const dealId = getDealId(productPage, config);
    const deal = config.dealBars.find((item) => item.id === dealId);
    if (!deal) return null;

    const unitPrice = getUnitPriceCents(productPage);
    if (!unitPrice) return null;

    const selectedBar = getSelectedBar(productPage, dealId);
    const quantity = getLiveQuantity(selectedBar, deal.quantity || 1);
    const barItemPrice = getBarItemPrice(selectedBar, unitPrice, quantity);
    const itemPrice = barItemPrice || getConfigItemPrice(deal, unitPrice);

    if (PRICE_MODE === 'total') {
      const fullPriceCents = unitPrice * quantity;
      const totalCents = itemPrice * quantity;
      return {
        dealId,
        priceCents: totalCents,
        compareCents: totalCents < fullPriceCents ? fullPriceCents : 0,
      };
    }

    return {
      dealId,
      priceCents: itemPrice,
      compareCents: itemPrice < unitPrice ? unitPrice : 0,
    };
  }

  function orderCompareBeforePrice(priceContainer) {
    const original = priceContainer.querySelector('[data-js-product-price-original]');
    const compare = priceContainer.querySelector('[data-js-product-price-compare]');
    if (original && compare && compare.compareDocumentPosition(original) & Node.DOCUMENT_POSITION_FOLLOWING) {
      priceContainer.insertBefore(compare, original);
    }
  }

  function updateNativePrices(productPage, priceCents, compareCents) {
    const moneyFormat =
      window.KROWN?.settings?.shop_money_format || '€{{amount_with_comma_separator}}';
    const formattedPrice = formatMoney(priceCents, moneyFormat);
    const formattedCompare =
      compareCents > priceCents ? formatMoney(compareCents, moneyFormat) : '';

    const priceContainers = [
      productPage.querySelector('[data-js-product-price-compact] .product-price'),
      productPage.querySelector('[data-js-sticky-price] .product-price'),
    ].filter(Boolean);

    priceContainers.forEach((priceContainer) => {
      const original = priceContainer.querySelector('[data-js-product-price-original]');
      const compare = priceContainer.querySelector('[data-js-product-price-compare]');

      if (original) {
        original.textContent = formattedPrice;
      }

      if (compare) {
        compare.innerHTML = formattedCompare ? `<span>${formattedCompare}</span>` : '';
        if (formattedCompare) {
          orderCompareBeforePrice(priceContainer);
        }
      }
    });

    const stickyPrice = productPage.querySelector('[data-js-sticky-price]');
    if (stickyPrice && !stickyPrice.querySelector('[data-js-product-price-original]')) {
      stickyPrice.innerHTML = formattedCompare
        ? `<div class="product-price"><del class="product-price--compare" data-js-product-price-compare><span>${formattedCompare}</span></del><span class="product-price--original" data-js-product-price-original>${formattedPrice}</span></div>`
        : `<div class="product-price"><span class="product-price--original" data-js-product-price-original>${formattedPrice}</span></div>`;
    }
  }

  function syncBundlePrice(productPage) {
    const config = getDealConfig(productPage);
    if (!config?.dealBars?.length) return false;

    const prices = getDealPrices(productPage, config);
    if (!prices) return false;

    const syncKey = `${prices.dealId}-${prices.priceCents}-${prices.compareCents}`;
    if (lastSyncedByPage.get(productPage) === syncKey) {
      return true;
    }

    lastSyncedByPage.set(productPage, syncKey);
    updateNativePrices(productPage, prices.priceCents, prices.compareCents);
    return true;
  }

  function setupProductPage(productPage) {
    if (setupPages.has(productPage)) return;
    if (!hasKaching(productPage)) return;

    setupPages.add(productPage);

    let syncTimer = null;
    const scheduleSync = () => {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(() => syncBundlePrice(productPage), 0);
    };

    const pollStart = Date.now();
    const poll = () => {
      syncBundlePrice(productPage);
      if (Date.now() - pollStart < POLL_DURATION_MS) {
        setTimeout(poll, POLL_MS);
      }
    };
    poll();

    window.addEventListener('load', scheduleSync, { passive: true });

    const productVariants = productPage.querySelector('product-variants');
    if (productVariants) {
      productVariants.addEventListener('VARIANT_CHANGE', () => {
        lastSyncedByPage.delete(productPage);
        scheduleSync();
        setTimeout(scheduleSync, 50);
        setTimeout(scheduleSync, 250);
      });
    }

    const pageObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const target =
          mutation.target.nodeType === 3 ? mutation.target.parentElement : mutation.target;
        if (!target) continue;
        if (
          target.closest?.('.kaching-bundles, kaching-bundle, kaching-bundles-block') ||
          target.matches?.('.kaching-bundles__bar, .kaching-bundles__block, kaching-bundle') ||
          mutation.attributeName === 'data-initialized'
        ) {
          lastSyncedByPage.delete(productPage);
          scheduleSync();
          return;
        }
      }
    });

    pageObserver.observe(productPage, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: ['class', 'data-initialized', 'checked', 'value'],
    });

    const priceWrapper = productPage.querySelector('[data-js-product-price-compact]');
    if (priceWrapper) {
      const priceObserver = new MutationObserver(() => {
        const config = getDealConfig(productPage);
        if (!config) return;

        const prices = getDealPrices(productPage, config);
        if (!prices) return;

        const moneyFormat =
          window.KROWN?.settings?.shop_money_format || '€{{amount_with_comma_separator}}';
        const expected = formatMoney(prices.priceCents, moneyFormat);
        const current = priceWrapper.querySelector('[data-js-product-price-original]')?.textContent?.trim();

        if (current && current !== expected) {
          lastSyncedByPage.delete(productPage);
          scheduleSync();
        }
      });

      priceObserver.observe(priceWrapper, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    productPage.addEventListener(
      'input',
      (event) => {
        if (event.target.closest('.kaching-bundles, kaching-bundle')) {
          lastSyncedByPage.delete(productPage);
          scheduleSync();
        }
      },
      true
    );

    productPage.addEventListener(
      'change',
      (event) => {
        if (event.target.closest('.kaching-bundles, kaching-bundle')) {
          lastSyncedByPage.delete(productPage);
          scheduleSync();
        }
      },
      true
    );

    productPage.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('.kaching-bundles, kaching-bundle')) {
          lastSyncedByPage.delete(productPage);
          scheduleSync();
          /* the quantity buttons update the bar after the click, so sync again once it has */
          setTimeout(scheduleSync, 80);
          setTimeout(scheduleSync, 300);
        }
      },
      true
    );

    scheduleSync();
  }

  function init() {
    document.querySelectorAll('product-page').forEach(setupProductPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', init, { passive: true });
  document.addEventListener('shopify:section:load', init);

  if (!document.documentElement.dataset.kopstormBundlePriceWatcher) {
    document.documentElement.dataset.kopstormBundlePriceWatcher = 'true';
    const bodyObserver = new MutationObserver(init);
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
